import {
  Directive,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';

import Decimal from 'decimal.js-light';
import { InputNumber } from 'primeng/inputnumber';
import { AppContext } from '../../common/models/app-context';
import { isNullOrEmpty } from '../../core/utils/common-functions';
import * as NGUtils from '../../core/utils/ng-functions';

//#region p-inputNumber prototype
// Override p-inputNumber 檢核輸入值 function
InputNumber.prototype.validateValue = function (value) {
  if (value === '-' || value == null) {
    // Minus sign
    return null;
  }

  if (this.min !== null && value < this.min) {
    return this.min;
  }

  if (this.max !== null && value > this.max) {
    return this.max;
  }

  return value;
};

// Override p-inputNumber 更新輸入框顯示值 function
InputNumber.prototype.updateInput = function (
  value,
  insertedValueStr,
  operation,
  anyValue?: any
) {
  insertedValueStr = insertedValueStr || '';

  let inputValue = this.input.nativeElement.value;
  let newValue = this.formatValue(value);
  let currentLength = inputValue.length;

  if (currentLength === 0) {
    this.input.nativeElement.value = newValue;
    this.input.nativeElement.setSelectionRange(0, 0);
    this.initCursor();
    const prefixLength = (this.prefixChar || '').length;
    const selectionEnd = prefixLength + insertedValueStr.length;
    this.input.nativeElement.setSelectionRange(selectionEnd, selectionEnd);
  } else {
    let selectionStart = this.input.nativeElement.selectionStart;
    let selectionEnd = this.input.nativeElement.selectionEnd;
    if (
      this.maxlength &&
      this.maxlength < newValue.replace(/\D+/g, '').length
    ) {
      return;
    }

    this.input.nativeElement.value = newValue;
    let newLength = newValue.length;

    if (operation === 'range-insert') {
      const startValue = this.parseValue(
        (inputValue || '').slice(0, selectionStart)
      );
      const startValueStr = startValue !== null ? startValue.toString() : '';
      const startExpr = startValueStr.split('').join(`(${this.groupChar})?`);
      const sRegex = new RegExp(startExpr, 'g');
      const testResult = sRegex.test(newValue);

      const tExpr = insertedValueStr.split('').join(`(${this.groupChar})?`);
      const tRegex = new RegExp(tExpr, 'g');
      const testResulSlice = tRegex.test(newValue.slice(sRegex.lastIndex));
      if (!(testResult && testResulSlice)) {
        console.log('input-number-directive', testResult);
      }
      selectionEnd = sRegex.lastIndex + tRegex.lastIndex;
      this.input.nativeElement.setSelectionRange(selectionEnd, selectionEnd);
    } else if (newLength === currentLength) {
      if (operation === 'insert' || operation === 'delete-back-single')
        this.input.nativeElement.setSelectionRange(
          selectionEnd + 1,
          selectionEnd + 1
        );
      else if (operation === 'delete-single')
        this.input.nativeElement.setSelectionRange(
          selectionEnd - 1,
          selectionEnd - 1
        );
      else if (operation === 'delete-range' || operation === 'spin')
        this.input.nativeElement.setSelectionRange(selectionEnd, selectionEnd);
    } else if (operation === 'delete-back-single') {
      let prevChar = inputValue.charAt(selectionEnd - 1);
      let nextChar = inputValue.charAt(selectionEnd);
      let diff = currentLength - newLength;
      let isGroupChar = this._group.test(nextChar);

      if (isGroupChar && diff === 1) {
        selectionEnd += 1;
      } else if (!isGroupChar && this.isNumeralChar(prevChar)) {
        selectionEnd += -1 * diff + 1;
      }

      this._group.lastIndex = 0;
      this.input.nativeElement.setSelectionRange(selectionEnd, selectionEnd);
    } else if (inputValue === '-' && operation === 'insert') {
      this.input.nativeElement.setSelectionRange(0, 0);
      const index = this.initCursor();
      const selectionEnd = index + insertedValueStr.length + 1;
      this.input.nativeElement.setSelectionRange(selectionEnd, selectionEnd);
    } else {
      selectionEnd = selectionEnd + (newLength - currentLength);
      this.input.nativeElement.setSelectionRange(selectionEnd, selectionEnd);
    }
  }

  this.input.nativeElement.setAttribute('aria-valuenow', value);
};

// Override p-inputNumber 上下跳動 function
InputNumber.prototype.spin = function (event, dir) {
  let step = this.step * dir;
  let currentValue = this.parseValue(this.input.nativeElement.value) || 0;
  let newValue = this.validateValue(currentValue + step);
  if (
    this.maxlength &&
    this.maxlength < this.formatValue(newValue).replace(/\D+/g, '').length
  ) {
    return;
  }

  this.updateInput(newValue, null, 'spin');
  this.updateModel(event, newValue);

  this.handleOnInput(event, currentValue, newValue);
};

// Override p-inputNumber 游標位置 function
InputNumber.prototype.initCursor = function () {
  let selectionStart = this.input.nativeElement.selectionStart;
  let inputValue = this.input.nativeElement.value;
  let valueLength = inputValue.length;
  let index = null as any;
  // remove prefix
  let prefixLength = (this.prefixChar || '').length;
  inputValue = inputValue.replace(this._prefix, '');
  selectionStart = selectionStart - prefixLength;
  let char = inputValue.charAt(selectionStart);
  if (this.isNumeralChar(char)) {
    return selectionStart + prefixLength;
  }
  //left
  let i = selectionStart - 1;
  while (i >= 0) {
    char = inputValue.charAt(i);
    if (this.isNumeralChar(char)) {
      index = i + prefixLength;
      break;
    } else {
      i--;
    }
  }
  if (index !== null) {
    this.input.nativeElement.setSelectionRange(index + 1, index + 1);
  } else {
    i = selectionStart;
    while (i < valueLength) {
      char = inputValue.charAt(i);
      if (this.isNumeralChar(char)) {
        index = i + prefixLength;
        break;
      } else {
        i++;
      }
    }
    if (index !== null) {
      this.input.nativeElement.setSelectionRange(index, index);
    }
  }
  return index || 0;
};
//#endregion

/**
 * 數值輸入框(p-inputNumber)元件設定
 * 註:預設以金額設定為主
 *
 * @export
 * @class InputNumberSettingDirective
 * @implements {OnChanges}
 */
@Directive({
  selector: '[sigvInputNumberSetting]',
})
export class InputNumberSettingDirective implements OnInit, OnChanges {
  /**
   * 小數點設定
   */
  private digitInfo: {
    minInt: number;
    minFraction: number;
    maxFraction: number;
  };

  /**
   * 小數最小位數
   */
  @Input() minFractionDigits: number;

  /**
   * 小數最大位數
   */
  @Input() maxFractionDigits: number;

  /**
   * 整數最大長度
   */
  @Input() maxlength: number;

  /**
   * 只顯示整數
   */
  @Input() numberOnly = true;

  /**
   * 允許負數
   */
  @Input() allowNegative = false;

  /**
   * 進位方式
   * 註:off - 四捨五入; up: 無條件進位; down: 無條件捨去
   */
  @Input() round?: string;

  /**
   * 進位單位
   */
  @Input() step?: number;

  constructor(
    private inputNumber: InputNumber,
    private _appContext: AppContext
  ) {
    this.digitInfo = NGUtils.extractDigitsInfo(this.envConfig.format.decimal);
    this.maxlength = this.envConfig.format.numberMaxLength ?? 15;
    this.minFractionDigits = this.digitInfo.minFraction ?? 2;
    this.maxFractionDigits = this.digitInfo.maxFraction ?? 2;
  }

  get envConfig(): any {
    return this._appContext.envConfig;
  }

  //#region Angular Lifecycle Hooks
  ngOnChanges(changes: SimpleChanges): void {
    // 偵測 numberOnly 值的變化
    if (changes['numberOnly']) {
      // 含小數
      if (!this.numberOnly) {
        this.inputNumber.minFractionDigits = this.minFractionDigits ?? 2;
        this.inputNumber.maxFractionDigits = this.maxFractionDigits ?? 2;
        this.inputNumber.maxlength =
          this.maxlength + (this.maxFractionDigits ?? 2);
      }
    }

    // 偵測 minFractionDigits 值的變化
    if (changes['minFractionDigits']) {
      // 含小數
      if (!this.numberOnly) {
        this.inputNumber.minFractionDigits = this.minFractionDigits;
      }
    }

    // 偵測 maxFractionDigits 值的變化
    if (changes['maxFractionDigits']) {
      // 含小數
      if (!this.numberOnly) {
        this.inputNumber.maxFractionDigits = this.maxFractionDigits;
        this.inputNumber.maxlength =
          this.maxlength + (this.maxFractionDigits ?? 0);
      }
    }

    // 偵測 maxlength 值的變化
    if (changes['maxlength']) {
      this.inputNumber.maxlength =
        this.maxlength + (this.numberOnly ? 0 : this.maxFractionDigits ?? 2);
    }
  }

  ngOnInit(): void {
    this.init();
    this.inputNumber.onBlur.subscribe({
      next: (event: any) => {
        if (this.inputNumber.step <= 0) {
          return;
        }
        this.updateInputValue();
      },
    });
  }
  //#endregion Angular Lifecycle Hooks

  private init(): void {
    // 含小數
    if (!this.numberOnly) {
      this.inputNumber.minFractionDigits = this.minFractionDigits ?? 2;
      this.inputNumber.maxFractionDigits = this.maxFractionDigits ?? 2;
    }
    this.inputNumber.maxlength =
      this.maxlength + (this.numberOnly ? 0 : this.maxFractionDigits);
    this.inputNumber.min = this.allowNegative
      ? (null as any)
      : (this.inputNumber.min ?? 0) > 0
      ? this.inputNumber.min
      : 0;

    this.step = this.step ?? this.envConfig.format.numberStep;
    if (isNullOrEmpty(this.step) === false || this.step != -1) {
      this.inputNumber.step = this.step!;
    }

    this.round = this.round ?? this.envConfig.format.numberRound;
  }

  private updateInputValue(): void {
    const tmpValue = this.inputNumber.value;
    const tmpRound = this.round as string;
    const tmpStep = this.inputNumber.step;
    const newValue = this.calculateRoundValue(tmpStep, tmpRound, tmpValue);
    this.inputNumber.updateInput(newValue, null, 'spin');
    this.inputNumber.updateModel(null, newValue);
  }

  private calculateRoundValue(
    step: number,
    round: string,
    value: number
  ): number {
    let num = 0;

    if (isNullOrEmpty(value) === false) {
      switch (round) {
        case 'up': // 無條件進位
          num = Math.ceil(
            new Decimal(this.inputNumber.value).dividedBy(step).toNumber()
          );
          break;
        case 'down': // 無條件捨去
          num = Math.floor(
            new Decimal(this.inputNumber.value).dividedBy(step).toNumber()
          );
          break;
        case 'off': // 四捨五入
        default: // 預設
          num = Math.round(
            new Decimal(this.inputNumber.value).dividedBy(step).toNumber()
          );
          break;
      }
      value = new Decimal(this.inputNumber.step).mul(num).toNumber();
    }

    let newValue = this.inputNumber.validateValue(value);

    // 進位後如果長度大於最大長度就往後退
    if (
      this.inputNumber.maxlength &&
      this.inputNumber.maxlength <
        this.inputNumber.formatValue(newValue).replace(/\D+/g, '').length
    ) {
      value = new Decimal(this.inputNumber.step).mul(num - 1).toNumber();
      newValue = this.inputNumber.validateValue(value);
    }

    return newValue;
  }
}
